<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="user.isDiff"
    :isOutdated="isOutdated"
    :itemType="'USER'"
    :typename="'user'"
    :item="user"
    :onUpdate="updateUser"
    :canUpdate="$can('edit', 'user', user.name)"
    :onCreate="createUser"
    :onDelete="deleteUser"
    :canDelete="$can('delete', 'user', user.name)"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
    :isEdited="isUserEdited"
    :isValidated="!$v.$invalid"
  >
    <fieldset slot="fieldset" :disabled="isOutdated">
      <div
        class="field required"
        v-diff-field="userDiff.name"
        :class="{ error: $v.user.name.$error }"
      >
        <label for="userName">{{ $t('NAME') }}</label>
        <input
          id="userName"
          type="text"
          :placeholder="$t('NAME')"
          v-model.trim="user.name"
          @click="$v.user.name.$touch()"
          :disabled="isEditMode"
        />
      </div>
      <template v-if="showPasswordPanel">
        <div class="ui attached visible info message" v-if="!isEditMode">
          {{ $t('CREATEUSERWITHPASSWORD_HELP') }}
        </div>
        <div class="ui attached visible info message" v-else>
          {{ $t('RESETUSERPASSWORD_HELP') }}
        </div>
        <div class="ui form attached fluid segment">
          <div class="two fields">
            <div class="field" :class="{ error: $v.user.password.$error }">
              <label for="password">{{ $t('PASSWORD') }}</label>
              <input
                id="password"
                type="password"
                autocomplete="new-password"
                :placeholder="$t('PASSWORD')"
                @focus="$v.user.password.$touch()"
                v-model.trim="user.password"
                :disabled="user.isDiff"
              />
              <password
                v-if="user.password"
                v-model.trim="user.password"
                :strength-meter-only="true"
                @score="onPasswordScore"
                @feedback="onPasswordFeedback"
              />
            </div>
            <div class="field" :class="{ error: $v.repeatedPassword.$error }">
              <label for="repeatPassword">{{ $t('REPEATPASSWORD') }}</label>
              <input
                id="repeatPassword"
                type="password"
                autocomplete="new-password"
                :placeholder="$t('PASSWORD')"
                @focus="$v.repeatedPassword.$touch()"
                v-model.trim="repeatedPassword"
                :disabled="user.isDiff"
              />
            </div>
          </div>
        </div>
        <div
          class="ui attached bottom visible warning message"
          v-if="passwordFeedback.suggestions.length > 0 || passwordFeedback.warning"
        >
          <div class="header">{{ $t('PASSWORDSUGGESTIONS') }}</div>
          <ul class="list">
            <li v-if="passwordFeedback.warning">
              <b>{{ passwordFeedback.warning }}</b>
            </li>
            <li v-for="s in passwordFeedback.suggestions" :key="s">{{ s }}</li>
          </ul>
        </div>
        <div class="ui attached bottom visible icon success message" v-else>
          <i class="thumbs up outline small icon"></i>
          <div class="content">
            <p>{{ $t('PASSWORD_OK') }}</p>
          </div>
        </div>
        <div class="field" v-if="user.password" v-diff-field="userDiff.super">
          <div class="ui toggle checkbox">
            <input
              id="userIsAdmin"
              type="checkbox"
              v-model="user.super"
              :disabled="!canMakeSuper || user.isDiff || (isEditMode && !user.password)"
            />
            <label for="userIsAdmin">
              {{ $t('USER_IS_ADMIN') }}
            </label>
          </div>
        </div>
      </template>
      <div class="field" v-diff-field.disable="userDiff.fullname">
        <label for="fullName">{{ $t('FULLNAME') }}</label>
        <input
          id="fullName"
          type="text"
          :placeholder="$t('FULLNAME')"
          v-model.trim="user.fullname"
        />
      </div>
      <div
        class="field"
        v-diff-field.disable="userDiff.email"
        :class="{ error: $v.user.email.$error }"
      >
        <label for="email">{{ $t('EMAIL') }}</label>
        <input
          id="email"
          type="text"
          :placeholder="$t('EMAIL')"
          @focus="$v.email.$touch()"
          v-model.trim="user.email"
        />
      </div>
      <div class="field">
        <group-typeahead
          :allGroups="groups"
          :currentGroups="editedGroups"
          @selected="groupSelected"
          @created="groupCreated"
          @removed="groupRemoved"
          :disabled="user.isDiff || !groups"
        />
      </div>
      <!-- FLOW LIST -->
      <div id="flowListContainer">
        <add-delete-list
          id="flowList"
          :class="{ transparentField: user.isDiff }"
          :icon="'random'"
          :type="'FLOW'"
          :isDisabled="user.isDiff"
          :allItems="allFlows"
          :currentItems="currentUserFlows"
          :onRemove="removeFlow"
          :onAdd="addFlow"
          :to="
            f => {
              return {
                name: 'app-flow',
                params: { appId: f.application, flowId: f.id, mode: 'auto' }
              }
            }
          "
        />
      </div>
      <div class="ui error visible message" v-if="$v.$invalid">
        <div class="header">{{ $t('VALIDATIONERRORS') }}</div>
        <ul class="list">
          <li v-if="!$v.user.password.mustBeComplexOrEmpty">{{ $t('PASSWORDTOOSIMPLE') }}</li>
          <li v-if="!$v.repeatedPassword.sameAsPassword">{{ $t('REPEATPASSWORD') }}</li>
          <li v-if="!$v.user.name.required">{{ $t('MISSINGUSERNAME') }}</li>
          <li v-if="!$v.user.name.mustHaveUniqueUsername">
            {{ $t('USERNAMETAKEN', { username: user.name }) }}
          </li>
        </ul>
      </div>
    </fieldset>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
#flowListContainer {
  text-align: center;
}
#flowList {
  display: inline-block;
  width: 50%;
  text-align: left;
}
</style>

<script>
// @flow
import Vue from 'vue'
import Password from 'vue-password-strength-meter'
import { Flow, User, Group, Toast, Typenames, ErrorCodes, UnsavedChecker, Auth } from 'services'
import { AddDeleteList, GroupTypeahead, MainSection } from 'ui'
import { map, catchError } from 'rxjs/operators'
import { required, minLength, sameAs, email } from 'vuelidate/lib/validators'
import _ from 'lodash'
import { ActionTypes } from 'store'
import GroupAffiliated from '../Mixins/GroupAffiliated'
const user = {
  name: ''
}

export default {
  name: 'user',
  mixins: [UnsavedChecker.mixin, GroupAffiliated],
  components: { MainSection, GroupTypeahead, AddDeleteList, Password },

  props: ['userId'],

  data() {
    return {
      mode: '',
      user: { ...user },
      allUsers: [],
      isOutdated: false,
      userDiff: {},
      userFlows: [],
      modifiedUserFlows: [],
      passwordScore: 0,
      passwordFeedback: { warning: '', suggestions: [] },
      repeatedPassword: ''
    }
  },

  computed: {
    showPasswordPanel() {
      return (
        _.includes(this.$store.state.config.features, 'user-management') &&
        (this.canEditUser || (this.isMe && this.originalPassword))
      )
    },

    canMakeSuper() {
      return this.$can('make', 'super')
    },

    canEditUser() {
      return this.$can('edit', 'user', this.user.name)
    },

    isMe() {
      return this.user.name == Auth.getLoggedInUser()
    },
    isUserEdited() {
      return this.areGroupsEdited || !_.isEmpty(...this.modifiedUserFlows)
    },
    savedGroups() {
      return this.user.id ? this.$store.getters['group/getItemsByUser'](this.user.id) : []
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    allFlows() {
      return this.$store.state.flow.items
    },
    currentUserFlows() {
      return _.filter(
        [
          ..._.filter(
            this.userFlows,
            f => !_.includes(this.modifiedUserFlows.map(f => f.id), f.id)
          ),
          ...this.modifiedUserFlows
        ],
        f => f.maintainer
      )
    }
  },

  validations() {
    const mustBeComplexOrEmpty = value => !this.user.password || this.passwordScore >= 2
    const sameAsPassword = value => value === this.user.password || !value

    return {
      user: {
        name: {
          required,
          mustHaveUniqueUsername: value =>
            this.isEditMode || !_.some(this.allUsers, i => i.name == value)
        },
        password: {
          mustBeComplexOrEmpty
        },
        email: {
          email
        }
      },
      repeatedPassword: {
        sameAsPassword
      }
    }
  },

  methods: {
    onPasswordFeedback(feedback) {
      this.passwordFeedback = feedback
    },

    onPasswordScore(score) {
      this.passwordScore = score
    },

    readUser(userId: string) {
      return User.readUser(userId)
        .then(user => {
          this.user = user
          this.resetGroupChanges()
          this.isOutdated = false
          this.passwordFeedback = { warning: '', suggestions: [] }
          this.passwordScore = 4
          this.originalPassword = user.password
          this.repeatedPassword = user.password
          this.$v.$touch()
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('USER', 1) })
            })
          )
          console.error(err)
        })
    },

    updateUser() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          return User.updateUser(this.user.id, this.user)
            .then(res => {
              this.user.version = res.version
              return this.saveGroups('user', this.user.id)
            })
            .then(() => {
              return Promise.all(this.modifiedUserFlows.map(flow => Flow.updateFlow(flow.id, flow)))
            })
            .then(() => {
              this.userFlows = this.currentUserFlows
              this.modifiedUserFlows = []
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('USER', 1) }), {
                remove: 5000
              })
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('USER', 1) }),
                    thing: this.$i18n.tc('USER', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('USER', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('USER', 1) }),
              thing: this.$i18n.tc('USER', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('USER', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    createUser() {
      if (!this.$v.$invalid) {
        let userId
        return User.createUser(this.user)
          .then(res => {
            userId = res.id
            this.user.version = res.version
            const promises = this.editedGroups.map(group =>
              Group.addAffiliate('user', group.id, userId)
            )
            return Promise.all(promises)
          })
          .then(() => {
            this.resetGroupChanges()
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('USER', 1) }), {
              remove: 5000
            })
            this.$router.push({ name: 'user', params: { userId: userId } })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('USER', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('USER', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteUser() {
      Promise.all(this.savedGroups.map(g => Group.removeAffiliate('user', g.id, this.user.id)))
        .then(() => User.deleteUser(this.user.id))
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('USER', 1) }), {
            remove: 5000
          })
          this.$router.push({ name: 'users' })
        })
        .catch(err => {
          const errMessage = this.$i18n.t('ERR', {
            action: this.$i18n.t('DELETE', {
              thing: this.$i18n.tc('USER', 1)
            })
          })
          Toast.error(errMessage, { text: this.$i18n.t('TRY_AGAIN') })
          console.error(err)
        })
    },

    addFlow(flow) {
      let addedFlow = { ...flow, maintainer: this.user.id }
      this.modifiedUserFlows = [
        ..._.filter(this.modifiedUserFlows, f => f.id !== flow.id),
        addedFlow
      ]
    },

    removeFlow(flow) {
      let removedFlow = { ...flow, maintainer: '' }
      this.modifiedUserFlows = [
        ..._.filter(this.modifiedUserFlows, f => f.id !== flow.id),
        removedFlow
      ]
    },

    editMode() {
      this.mode = 'edit'
      this.readUser(this.userId)
      Flow.readFlowsForMaintainer(this.userId).then(userFlows => {
        this.userFlows = userFlows
      })
    },

    createMode() {
      this.mode = 'create'
      this.user = { ...user }
      User.readAllUsers().then(users => (this.allUsers = users))
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.user = changedItem
      this.userDiff = fullDiff
    },
    onRevert(user) {
      // Keep the user preferences on revert since they are edited and tracked for changes in another view
      this.user = _.extend({}, user, { preferences: this.user.preferences })
      this.userDiff = []
    }
  },

  mounted() {
    if (this.userId) {
      this.editMode()
    } else {
      this.createMode()
    }
  },

  watch: {
    userId: function(newUserId) {
      this.$v.$reset()
      if (newUserId) {
        this.editMode()
      } else {
        this.passwordFeedback = { warning: '', suggestions: [] }
        this.passwordScore = 4
        this.originalPassword = ''
        this.repeatedPassword = ''
        this.$v.$touch()
        this.createMode()
      }
    }
  }
}
</script>
